import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { fetchTermsVersions } from "../services/api";
import { TermsDocId, TermsOfUse } from "../types/terms";
import Markdown from "react-markdown";
import axios from "axios";

// Estado unificado via reducer
interface State {
  loading: boolean;
  contentLoading: boolean;
  error: string | null;
  items: TermsOfUse[];
  selected: TermsOfUse | null;
  content: string;
}

type Action =
  | { type: "FETCH_VERSIONS_START" }
  | {
      type: "FETCH_VERSIONS_SUCCESS";
      payload: { items: TermsOfUse[]; selected: TermsOfUse | null };
    }
  | { type: "FETCH_VERSIONS_ERROR"; payload: string }
  | { type: "FETCH_CONTENT_START" }
  | { type: "FETCH_CONTENT_SUCCESS"; payload: string }
  | { type: "FETCH_CONTENT_ERROR"; payload: string }
  | { type: "SET_SELECTED"; payload: TermsOfUse | null };

const initialState: State = {
  loading: true,
  contentLoading: false,
  error: null,
  items: [],
  selected: null,
  content: "",
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_VERSIONS_START":
      return { ...state, loading: true, error: null };
    case "FETCH_VERSIONS_SUCCESS":
      return {
        ...state,
        loading: false,
        items: action.payload.items,
        selected: action.payload.selected,
      };
    case "FETCH_VERSIONS_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "FETCH_CONTENT_START":
      return { ...state, contentLoading: true, error: null };
    case "FETCH_CONTENT_SUCCESS":
      return { ...state, contentLoading: false, content: action.payload };
    case "FETCH_CONTENT_ERROR":
      return {
        ...state,
        contentLoading: false,
        error: action.payload,
        content: "",
      };
    case "SET_SELECTED":
      return { ...state, selected: action.payload };
    default:
      return state;
  }
}

type TermsListProps = {
  id: TermsDocId;
  title: string;
};

const TermsList: React.FC<TermsListProps> = ({ id, title }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const { loading, contentLoading, error, items, selected, content } = state;

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const latestVersion = React.useMemo(() => {
    if (!items.length) return undefined;
    return items.reduce(
      (max, cur) => (cur.version > max ? cur.version : max),
      items[0].version
    );
  }, [items]);

  // Carregar versões com async/await e AbortController
  React.useEffect(() => {
    const controller = new AbortController();
    const loadVersions = async () => {
      dispatch({ type: "FETCH_VERSIONS_START" });
      try {
        const res = await fetchTermsVersions(id, controller.signal);
        const latest = res.terms.reduce<TermsOfUse | null>((acc, cur) => {
          if (!acc || cur.version > acc.version) return cur;
          return acc;
        }, null);
        dispatch({
          type: "FETCH_VERSIONS_SUCCESS",
          payload: {
            items: res.terms,
            selected: latest ?? res.terms[0] ?? null,
          },
        });
      } catch (e) {
        if (controller.signal.aborted) return;
        dispatch({
          type: "FETCH_VERSIONS_ERROR",
          payload: "Não foi possível carregar as versões.",
        });
      }
    };
    loadVersions();
    return () => controller.abort();
  }, [id]);

  // Carregar conteúdo do termo selecionado
  React.useEffect(() => {
    if (!selected) {
      dispatch({ type: "FETCH_CONTENT_SUCCESS", payload: "" });
      return;
    }
    const controller = new AbortController();
    const loadContent = async () => {
      dispatch({ type: "FETCH_CONTENT_START" });
      try {
        const res = await axios.get<string>(selected.link, {
          signal: controller.signal,
        });
        dispatch({ type: "FETCH_CONTENT_SUCCESS", payload: res.data });
      } catch (e) {
        if (controller.signal.aborted) return;
        dispatch({
          type: "FETCH_CONTENT_ERROR",
          payload: "Não foi possível carregar o conteúdo do termo.",
        });
      }
    };
    loadContent();
    return () => controller.abort();
  }, [selected]);

  // Handlers
  const handleVersionClick = (item: TermsOfUse) => {
    dispatch({ type: "SET_SELECTED", payload: item });
  };
  const handleVersionChange = (version: number) => {
    const found = items.find((i) => i.version === version) || null;
    dispatch({ type: "SET_SELECTED", payload: found });
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Typography variant="h4" gutterBottom mb="1em">
        {title}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading state com skeletons para uma experiência mais suave */}
      {loading && !error && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="stretch"
        >
          <Paper elevation={1} sx={{ p: 2, minWidth: { xs: "100%", md: 320 } }}>
            <Typography variant="subtitle1" gutterBottom>
              Versões
            </Typography>
            <Stack spacing={1.5}>
              {[...Array(5)].map((_, i) => (
                <Box key={i}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="40%" />
                      <Skeleton width="60%" />
                    </Box>
                  </Stack>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper
            elevation={1}
            sx={{ p: { xs: 1.5, md: 2 }, flex: 1, minHeight: 420 }}
          >
            <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={360} />
          </Paper>
        </Stack>
      )}

      {!loading && !error && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {isMdUp ? (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                minWidth: 320,
                position: "sticky",
                top: 80,
                alignSelf: "flex-start",
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Versões
              </Typography>
              <List dense sx={{ width: "100%" }}>
                {items.map((t) => {
                  const isSelected =
                    selected?.id === t.id && selected?.version === t.version;
                  return (
                    <React.Fragment key={`${t.id}-${t.version}`}>
                      <ListItem disableGutters>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleVersionClick(t)}
                          sx={{ borderRadius: 1, pr: 2 }}
                        >
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body1">
                                  v{t.version}
                                </Typography>
                                {latestVersion !== undefined &&
                                  t.version === latestVersion && (
                                    <Chip
                                      label="Atual"
                                      size="small"
                                      color="primary"
                                    />
                                  )}
                              </Stack>
                            }
                            secondary={t.id}
                          />
                        </ListItemButton>
                      </ListItem>
                      <Divider component="li" sx={{ my: 0.5 }} />
                    </React.Fragment>
                  );
                })}
                {items.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Nenhuma versão encontrada" />
                  </ListItem>
                )}
              </List>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ p: 2, width: "100%" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="versao-label">Versão</InputLabel>
                <Select
                  labelId="versao-label"
                  label="Versão"
                  value={selected ? selected.version : ""}
                  onChange={(e) => handleVersionChange(Number(e.target.value))}
                >
                  {items
                    .slice()
                    .sort((a, b) => b.version - a.version)
                    .map((opt) => (
                      <MenuItem
                        key={`${opt.id}-${opt.version}`}
                        value={opt.version}
                      >
                        v{opt.version}
                        {latestVersion !== undefined &&
                        opt.version === latestVersion
                          ? "  • Atual"
                          : ""}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Paper>
          )}

          {/* Painel de visualização */}
          <Paper
            elevation={1}
            sx={{ p: { xs: 1.5, md: 2 }, flex: 1, width: "100%" }}
          >
            {selected ? (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1">
                      Visualizando: v{selected.version}
                    </Typography>
                    {latestVersion !== undefined &&
                      selected.version === latestVersion && (
                        <Chip label="Atual" size="small" color="primary" />
                      )}
                  </Stack>
                </Stack>
                <Box flex={1}>
                  {contentLoading ? (
                    <Stack spacing={1}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="rectangular" height={200} />
                    </Stack>
                  ) : (
                    <Markdown>{content}</Markdown>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Selecione uma versão para visualizar.
                </Typography>
              </Box>
            )}
          </Paper>
        </Stack>
      )}
    </Container>
  );
};

export default TermsList;
